/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Change this flow with Seep": describe a change, get a proposal and the
 * list of what it changes (POST /automations/ai/edit, which saves nothing),
 * then Apply it to the canvas or Discard it. Applying still leaves the
 * automation unsaved: Seep proposes, the user confirms.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Sparkles } from "lucide-react";
import { useAuth } from "auth/Auth";
import { v2 as t } from "styles/tokens/v2";

export default function SeepEditBox({ enabled, disabledReason, buildAutomation, onApply }) {
  const { axiosInstance } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState("");

  const ask = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axiosInstance.post("/automations/ai/edit", { automation: buildAutomation(), prompt: prompt.trim() });
      setProposal(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Seep could not propose a change.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ m: 2.25, mt: 0, p: 1.75, borderRadius: "10px", bgcolor: "#F5F1FD", border: "1px solid #E4DAFA" }}>
      <Typography sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 14, fontWeight: 600, color: "#5B34B8" }}>
        <Sparkles size={15} /> Change this flow with Seep
      </Typography>
      {!proposal && (
        <>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={!enabled || busy}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={enabled ? "For example: wait two business days, then email them if they are still New." : disabledReason}
            inputProps={{ "aria-label": "Describe the change", maxLength: 2000 }}
            sx={{ mt: 1, bgcolor: t.surface }}
          />
          {enabled && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Button size="small" variant="contained" disabled={busy || prompt.trim().length < 3} onClick={ask} sx={{ bgcolor: "#5B34B8", color: "#FFFFFF", "&:hover": { bgcolor: "#4A2A99", color: "#FFFFFF" } }}>
                Propose
              </Button>
              {busy && <CircularProgress size={16} sx={{ color: "#5B34B8" }} />}
            </Box>
          )}
        </>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {proposal && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: t.text, fontWeight: 600 }}>Proposed changes</Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25, fontSize: 13.5, color: t.text }}>
            {(proposal.changes || []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </Box>
          {(proposal.warnings || []).map((w) => (
            <Typography key={w} sx={{ fontSize: 12.5, color: "#8A4B08", mt: 0.5 }}>{w}</Typography>
          ))}
          <Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!(proposal.changes || []).length}
              onClick={() => {
                onApply(proposal.proposal);
                setProposal(null);
                setPrompt("");
              }}
              sx={{ bgcolor: "#5B34B8", color: "#FFFFFF", "&:hover": { bgcolor: "#4A2A99", color: "#FFFFFF" } }}
            >
              Apply to canvas
            </Button>
            <Button size="small" onClick={() => setProposal(null)} sx={{ color: "#5B34B8" }}>
              Discard
            </Button>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: t.text2, mt: 0.75 }}>Applying changes the canvas only. Check it, then Save.</Typography>
        </Box>
      )}
    </Box>
  );
}

SeepEditBox.propTypes = {
  enabled: PropTypes.bool.isRequired,
  disabledReason: PropTypes.string,
  buildAutomation: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};
SeepEditBox.defaultProps = { disabledReason: "" };
